import { useMemo } from 'react';

import { useTranslation } from 'react-i18next';
import { translations } from '@shippypro/translations';

import { SetupStepButton } from './components/SetupStepButton';

import { useGetSetupData } from '@web/features/_global/hooks/api/useGetSetupData';
import { useNavigate } from 'react-router-dom';

import { goToLegacy } from '@shippypro/utils';

/**
 * The setup banner, shows three buttons to let the user easily reach the corresponding setup step.
 * The third button is disabled until the first two steps are completed.
 *
 * @author Fabio Mezzina <fabio.mezzina@shippypro.com>
 * @author Valeria Curseri <valeria.curseri@shippypro.com>
 */
export const SetupBanner = () => {
  const { t } = useTranslation();

  const navigate = useNavigate();

  const {
    data: [, setup],
  } = useGetSetupData();

  const completedStep = useMemo(() => {
    if (setup) {
      if (setup.progress.storeCompleted && setup.progress.carrierCompleted)
        return 2;
      else if (setup.progress.storeCompleted || setup.progress.carrierCompleted)
        return 1;
      else return 0;
    } else return 0;
  }, [setup]);

  return (
    setup &&
    !setup.progress.setupCompleted && (
      <div>
        <div
          data-test="setup-banner"
          className="setup-banner flex justify-between items-center px-2 !py-2 bg-[color:--shp-color-bg-accent] "
        >
          <div
            data-test="setup-banner-title flex flex-col !gap-1"
            className="!text-white"
          >
            <span className="d-block font-semibold text-lg">
              {t(translations.banners.setup.title)}
            </span>
            <span className="d-block text-sm text-[#E1D6FF]">
              {t(translations.banners.setup.description, {
                step: completedStep,
              })}
            </span>
          </div>
          <div data-test="setup-banner-buttons" className="flex gap-2">
            <SetupStepButton
              icon="IconBuildingStore"
              isCompleted={setup.progress.storeCompleted}
              onClick={
                /* istanbul ignore next */
                () => goToLegacy('marketplace.html', true)
              }
              title={t(translations.banners.setup.stepButtons.stores.title)}
              description={t(
                translations.banners.setup.stepButtons.stores.description,
              )}
              step="stores"
            />
            <SetupStepButton
              icon="IconTruck"
              isCompleted={setup.progress.carrierCompleted}
              onClick={
                /* istanbul ignore next */
                () => goToLegacy('my-couriers.html', true)
              }
              title={t(translations.banners.setup.stepButtons.carriers.title)}
              description={t(
                translations.banners.setup.stepButtons.carriers.description,
              )}
              step="carriers"
            />
            <SetupStepButton
              icon="IconSend"
              isCompleted={setup.progress.shipCompleted}
              onClick={
                /* istanbul ignore next */
                () => navigate('/ship')
              }
              title={t(translations.banners.setup.stepButtons.ship.title)}
              tooltip={t(translations.banners.setup.stepButtons.ship.tooltip)}
              step="ship"
              disabled={
                !setup.progress.carrierCompleted ||
                !setup.progress.storeCompleted
              }
            />
          </div>
        </div>
      </div>
    )
  );
};
